import { useEffect, useState, useCallback } from "react";
import { format, addDays, parseISO, isAfter } from "date-fns";
import { ChevronLeft, ChevronRight, Check, Loader2, Lock, Save } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PILLARS, SELF_EXPRESSION_LINE } from "@/data/pillars";
import AiActionButton from "@/components/AiActionButton";

function PillarColumn({ pillar, entry, readOnly, onSave }) {
  const [note, setNote] = useState(entry.note || "");
  const [savedNote, setSavedNote] = useState(entry.note || "");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved

  useEffect(() => {
    setNote(entry.note || "");
    setSavedNote(entry.note || "");
    setSaveState("idle");
  }, [entry, pillar.id]);

  const persist = async (values) => {
    if (readOnly) return;
    const next = values.note ?? note;
    setSaveState("saving");
    try {
      await onSave(pillar.name, { note: next });
      setSavedNote(next);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("idle");
      toast.error(formatApiError(e));
    }
  };

  const dirty = note !== savedNote;

  return (
    <div
      className="flex flex-col rounded-xl bg-card border border-border p-5 min-w-[280px] md:min-w-0 transition-shadow duration-300 hover:shadow-[0_2px_16px_rgba(0,0,0,0.04)]"
      style={{ borderTop: `3px solid ${pillar.color}` }}
      data-testid={`pillar-column-${pillar.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pillar.color }} />
          <h3 className="font-serif text-xl text-foreground">{pillar.name}</h3>
        </div>
        <span className="h-4 flex items-center">
          {saveState === "saving" && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          {saveState === "saved" && <Check className="w-3.5 h-3.5 text-pillar-health" />}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{pillar.tagline}</p>

      {/* Weekly note */}
      <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">
        This week
      </label>
      <textarea
        className="journal-input text-sm min-h-[140px]"
        placeholder="What did you focus on this week?"
        value={note}
        disabled={readOnly}
        onChange={(e) => setNote(e.target.value)}
        data-testid={`pillar-${pillar.id}-note`}
      />

      {!readOnly && (
        <button
          type="button"
          onClick={() => persist({ note })}
          disabled={!dirty || saveState === "saving"}
          data-testid={`pillar-${pillar.id}-save`}
          className="mt-3 self-end inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm font-medium text-background disabled:opacity-40 disabled:cursor-not-allowed transition-opacity duration-200"
          style={{ backgroundColor: pillar.color, color: "#2C2C2C" }}
        >
          {saveState === "saving" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : saveState === "saved" && !dirty ? (
            <Check className="w-3.5 h-3.5" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          {saveState === "saved" && !dirty ? "Saved" : "Save"}
        </button>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [weekStart, setWeekStart] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/weeks/current")
      .then(({ data }) => {
        setCurrentWeek(data.week_start);
        setWeekStart(data.week_start);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const loadWeek = useCallback(async (ws) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/weeks/${ws}/entries`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (weekStart) loadWeek(weekStart);
  }, [weekStart, loadWeek]);

  const onSave = async (pillarName, values) => {
    await api.put(`/weeks/${weekStart}/pillars/${pillarName}`, values);
  };

  const goPrev = () => setWeekStart((ws) => format(addDays(parseISO(ws), -7), "yyyy-MM-dd"));
  const goNext = () => {
    const next = format(addDays(parseISO(weekStart), 7), "yyyy-MM-dd");
    if (currentWeek && isAfter(parseISO(next), parseISO(currentWeek))) return;
    setWeekStart(next);
  };
  const goCurrent = () => currentWeek && setWeekStart(currentWeek);

  const readOnly = data?.is_past;
  const canGoNext = currentWeek && weekStart && weekStart < currentWeek;

  const weekLabel = weekStart
    ? `${format(parseISO(weekStart), "dd/MM/yyyy")} – ${format(addDays(parseISO(weekStart), 6), "dd/MM/yyyy")}`
    : "";

  return (
    <div className="px-5 md:px-8 py-8 max-w-[1600px]">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
            Weekly Hub
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-foreground">Your week, across six pillars</h2>
          <p className="text-muted-foreground mt-2 max-w-2xl leading-relaxed">{SELF_EXPRESSION_LINE}</p>
        </div>
        <AiActionButton
          action="week_review"
          params={{ week_start: weekStart }}
          disabled={!weekStart}
          label="Analyse this week's progress"
          title="This week's reflection"
          description="A personal read of your six pillars this week, generated by your chosen AI."
          testId="analyse-week-btn"
          historyQuery={{ action: "week_review" }}
        />
      </div>

      {/* Week navigator */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            className="p-2 rounded-md hover:bg-secondary transition-colors duration-200"
            data-testid="week-prev"
            aria-label="Previous week"
          >
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
          <div className="text-center min-w-[220px]">
            <span className="font-serif text-lg text-foreground" data-testid="week-label">
              {weekLabel}
            </span>
          </div>
          <button
            onClick={goNext}
            disabled={!canGoNext}
            className="p-2 rounded-md hover:bg-secondary transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="week-next"
            aria-label="Next week"
          >
            <ChevronRight className="w-5 h-5 text-foreground" />
          </button>
        </div>

        <div className="flex items-center gap-3">
          {readOnly && (
            <span
              className="flex items-center gap-1.5 text-xs text-muted-foreground bg-secondary px-3 py-1.5 rounded-full"
              data-testid="readonly-badge"
            >
              <Lock className="w-3.5 h-3.5" /> Past week · read-only
            </span>
          )}
          {weekStart !== currentWeek && (
            <button
              onClick={goCurrent}
              className="text-sm text-foreground/80 hover:text-foreground underline underline-offset-4"
              data-testid="week-today"
            >
              This week
            </button>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4"
          data-testid="week-grid"
        >
          {PILLARS.map((pillar) => (
            <PillarColumn
              key={pillar.id}
              pillar={pillar}
              entry={data.entries[pillar.name]}
              readOnly={readOnly}
              onSave={onSave}
            />
          ))}
        </motion.div>
      )}
    </div>
  );
}
